<template>
  <Loading 
    :loading="isLoading"
    loading-text="Loading timezone settings..."
  >
    <div class="timezone-selector-container">
      <!-- Title -->
      <div class="title-section">
        <div class="title-icon-wrapper">
          <img src="@/assets/my-calendar/timezone-icon.svg" alt="timezone" class="timezone-icon" />
          <div class="section-title">Time Zone Settings</div>
        </div>
        <div class="section-description">
          Your calendar and appointment times will follow this time zone.
        </div>
      </div>

      <!-- Timezone Selector -->
      <div class="selector-wrapper">
        <Label label="Display my calendar in time zone:">
          <SingleSelector
            v-model="selectedTimezone"
            data-testid="my-calendar.setup-timezone-card.timezone-select"
            class="timezone-select"
            :options="timezoneOptions"
            size="medium"
            placeholder="Select timezone"
            @change="handleTimezoneChange"
          />
        </Label>
        <div v-if="selectedTimezone" data-testid="my-calendar.setup-timezone-card.current-timezone-text" class="current-timezone-label">
          {{ currentTimezoneLabel }}
        </div>
      </div>
    </div>
  </Loading>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { getBrowserTimezone, getTimezoneDisplayLabel, getTimezoneOptionsWithValue, normalizeTimezoneValue, FRIENDLY_TIMEZONE_OPTIONS } from '@/utils/timezones';
import { useStore } from 'vuex';
import Loading from '@/NewLibrary/Loading/Loading.vue';

const store = useStore();

// IANA timezone options (value is an IANA name, e.g. "America/Los_Angeles")
// with friendly labels, e.g. "Pacific Time (US & Canada) (UTC-8)"
const timezoneOptions = ref(FRIENDLY_TIMEZONE_OPTIONS);
const selectedTimezone = ref('');
// Raw timezone value returned by GET, used as old_timezone for the update call
const currentTimezone = ref('');

const isLoading = ref(false);
const isInitializing = ref(false);

// Friendly display label for the currently selected timezone
const currentTimezoneLabel = computed(() =>
  selectedTimezone.value ? getTimezoneDisplayLabel(selectedTimezone.value) : ''
);

onMounted(async () => {
  isLoading.value = true;
  isInitializing.value = true;
  try {
    const userTimezoneData = await store.dispatch('calendar/getUserTimezone');

    const tz =
      userTimezoneData?.timezone ||
      getBrowserTimezone() ||
      'America/New_York';

    // Keep the raw stored value for old_timezone audit, and select the resolved IANA zone
    // VERBATIM — getTimezoneOptionsWithValue injects it when it isn't a curated option, so
    // we never coerce (and later overwrite) the server's original zone.
    currentTimezone.value = userTimezoneData?.timezone || tz;
    const resolvedTz = normalizeTimezoneValue(tz);
    timezoneOptions.value = getTimezoneOptionsWithValue(resolvedTz);
    selectedTimezone.value = resolvedTz;
  } catch (err) {
    console.error(err)
    const fallbackTz = normalizeTimezoneValue(getBrowserTimezone());
    timezoneOptions.value = getTimezoneOptionsWithValue(fallbackTz);
    selectedTimezone.value = fallbackTz;
  } finally {
    isLoading.value = false;
    // Delay setting isInitializing to false to ensure v-model change is processed
    setTimeout(() => {
      isInitializing.value = false;
    }, 0);
  }
});

const handleTimezoneChange = async (value) => {
  // Ignore changes during initialization
  if (isInitializing.value) {
    return;
  }
  // Skip no-op confirms: if the selection resolves to the same zone already stored,
  // don't POST — otherwise we'd overwrite the server's original zone for no reason.
  if (normalizeTimezoneValue(value) === normalizeTimezoneValue(currentTimezone.value)) {
    return;
  }
  try {
    isLoading.value = true;
    // Save timezone to backend, passing the previous value as old_timezone for audit
    const result = await store.dispatch('calendar/updateUserTimezone', {
      timezone: value,
      old_timezone: currentTimezone.value,
    });
    // Track the newly saved timezone as the current value
    currentTimezone.value = result?.timezone || value;
    // Clear current event info to force refresh with new timezone
    store.commit('calendar/SET_CURRENT_EVENT_INFO', null);
  } catch (error) {
    console.error('Failed to update timezone:', error);
    // Optionally show error notification to user
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped>
.timezone-selector-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 24px;
  width: 100%;
}

.title-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.title-icon-wrapper {
  display: flex;
  gap: 10px;
  align-items: center;
}

.timezone-icon {
  width: 18px;
  height: 18px;
}

.section-title {
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 18px;
  line-height: 18px;
  color: #333;
  font-feature-settings: 'lnum' 1, 'tnum' 1;
}

.section-description {
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 12px;
  color: #999;
  padding: 1px 0;
  font-feature-settings: 'lnum' 1, 'tnum' 1;
}

.selector-wrapper {
  width: 494px;
}
.current-timezone-label {
  margin-top: 8px;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 13px;
  line-height: 16px;
  color: #666;
  font-feature-settings: 'lnum' 1, 'tnum' 1;
}

.timezone-select :deep(.el-select__wrapper) {
  height: 40px;
  --el-border-color: #C9C9C9;
  /* cursor: pointer; */
  /* padding: 4px 8px; */
}
</style>
